import React, { memo } from 'react';
import { Grid, IconButton } from '@mui/material';
import { useTranslation } from 'i18n';
import { useRouter } from 'next/router';
import { routes } from 'utils/routing';

import CalendarIcon from 'components/svg/icon-calendar-1.svg';
import { Event } from 'api/general/models';
import EventsSection from 'components/common/EventsSection';
import RenderComponent from '../common/RenderComponent';

export interface UpcomingAndPastEventsProps {
  organizationId: number;
  upcomingEvents: Event[];
  pastEvents: Event[];
  onToggleFavoriteEvent: () => void;
  isPastEventFetching: boolean;
  isUpcomingEventFetching: boolean;
}

const UpcomingAndPastEvents: React.FC<React.PropsWithChildren<UpcomingAndPastEventsProps>> = ({
  organizationId,
  upcomingEvents,
  pastEvents,
  onToggleFavoriteEvent,
  isPastEventFetching,
  isUpcomingEventFetching,
}) => {
  const [t] = useTranslation('public');
  const router = useRouter();

  const calendarButton = (
    <IconButton
      size="small"
      onClick={() => router.push(routes.organizationCalendar(organizationId).link)}
      sx={{ borderRadius: '50%', marginTop: '-5px' }}
    >
      <CalendarIcon color="red" />
    </IconButton>
  );

  const hasUpcomingEvents = upcomingEvents.length > 0;
  const hasPastEvents = pastEvents.length > 0;

  return (
    <Grid container spacing={6} justifyContent="center">
      <RenderComponent
        isLoading={isUpcomingEventFetching}
        isFetched={hasUpcomingEvents}
        renderContent={() => (
          <Grid item xs={12}>
            <EventsSection
              title={t<string>('organization.upcomingEvents')}
              events={upcomingEvents}
              itemsPerPage={100}
              onToggleFavoriteEvent={onToggleFavoriteEvent}
              titleAction={calendarButton}
              {...routes.upcomingEvents(organizationId)}
            />
          </Grid>
        )}
      />
      <RenderComponent
        isLoading={isPastEventFetching}
        isFetched={hasPastEvents}
        renderContent={() => (
          <Grid item xs={12}>
            <EventsSection
              title={t<string>('organization.pastEvents')}
              events={pastEvents}
              itemsPerPage={3}
              onToggleFavoriteEvent={onToggleFavoriteEvent}
              {...routes.pastEvents(organizationId)}
            />
          </Grid>
        )}
      />
    </Grid>
  );
};

export default memo(UpcomingAndPastEvents);
